import type { CommunicationRecord, PartnerOrganization } from "@entities";
import { PartnerOrganization as PartnerOrganizationEntity } from "@entities";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { Alert, Box, CircularProgress, Stack } from "@mui/material";
import {
  partnerOrganizationManagerRepo,
  partnerOrganizationRepo,
  userRepo,
} from "@repositories";
import { partnerOrganizationDeleteWaves } from "@shared/helpers/partner-organization-hierarchy";
import { removePartnerManagerAssociationRole } from "@shared/helpers/partner-organization-manager";
import { getLatestCommunicationRecord } from "@shared/helpers/communication-record";
import { useSessionStorage } from "@shared/frontend/hooks";
import {
  type ActiveFilter,
  useCreateHyperTable,
} from "@shared/frontend/hyper-table";
import { type ActionButton } from "@shared/frontend/hyper-table/inner-components/hyper-table-actions-bar/ActionsBar";
import type { SingleRowActionGetter } from "@shared/frontend/hyper-table/inner-components/hyper-table-menu-actions-column/HyperTableMenuActionsColumn";
import { isEntityFilter } from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/FilterMenuItem";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";
import { remult, repo, type EntityFilter } from "remult";
import { Roles } from "@shared/Roles";
import { useAlert } from "../../../contexts/AlertProvider";
import useQuestion from "../../../dialog-components/QuestionDialog";
import { TableHeaderTitles } from "../table-header/TableHeader";
import { TableToolbarTabs } from "../table-tabs/TableToolbarTabs";
import { OpportunitiesRegistrationTracking } from "../opportunities-table/OpportunitiesRegistrationTracking";
import { buildPartnerOrganizationColumns } from "./columns";
import { PARTNER_ORGANIZATION_TABLE_FILTERS } from "./filters";
import {
  PartnerOrganizationDialog,
  type PartnerOrganizationSavedResult,
} from "./PartnerOrganizationDialog";
import { PartnerOrganizationsCoverage } from "./PartnerOrganizationsCoverage";
import { PartnerOrganizationsMap } from "./PartnerOrganizationsMap";
import { PartnerOrganizationLeadsTable } from "./PartnerOrganizationLeadsTable";
import { PartnerOrganizationWorkDrawer } from "./PartnerOrganizationWorkDrawer";
import { loadPartnerOrganizationForDetails } from "./partner-organization-details.helper";
import type { PartnerOrganizationCoverageRow } from "./partnerOrganizationCoverageApi";
import {
  fetchPartnerOrganizationCatalogStats,
  type PartnerOrganizationActivity,
} from "./partner-organization-activity";
import { SingleRowAction } from "@shared/frontend/hyper-table/inner-components/hyper-table-expand-column/HyperTableExpandColumn";

const PARTNER_ORGANIZATIONS_DISPLAY_TYPE_KEY =
  "partner-organizations-display-type-v3";

const PARTNER_ORG_WORK_DRAWER_INCLUDE = {
  parent: true,
  managers: true,
  handlingManager: true,
  organizationManagers: {
    include: {
      manager: true,
    },
  },
  addresses: true,
} as const;

function filtersFingerprint(filters: ActiveFilter<PartnerOrganization>[]): string {
  return JSON.stringify(
    filters.map((f) => ({
      id: f.id,
      value: f.value,
      meta: f.meta,
      saveInSessionStorage: f.saveInSessionStorage,
    })),
  );
}

/** Upper bound for the partner organizations feeding the registration tracking view. */
const PARTNER_ORGANIZATIONS_TRACKING_LIMIT = 2000;

async function unlinkManagersForOrgs(orgIds: string[]) {
  if (orgIds.length === 0) return;
  const [appManagers] = await Promise.all([
    userRepo.find({
      where: { partnerOrganizationId: { $in: orgIds } },
    }),
    partnerOrganizationManagerRepo.deleteMany({
      where: { partnerOrganizationId: { $in: orgIds } },
    }),
  ]);
  await Promise.all(
    appManagers.map(async (m) => {
      await userRepo.update(m.id, { partnerOrganizationId: null });
      await removePartnerManagerAssociationRole(m.id);
    }),
  );
}

const PartnerOrganizationsTable = () => {
  const { HyperTable } = useCreateHyperTable(PartnerOrganizationEntity);
  const queryClient = useQueryClient();
  const canManage = remult.isAllowed(Roles.SuperAdmins);
  const { setAlertText, setSeverity, setTime } = useAlert();
  const question = useQuestion();
  const [selectedOrganization, setSelectedOrganization] =
    useState<PartnerOrganization>();
  const [isAddOrganization, setIsAddOrganization] = useState(false);
  const [createParent, setCreateParent] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [opportunitiesOrganization, setOpportunitiesOrganization] =
    useState<PartnerOrganization>();
  const [coverageDeadlineFrom, setCoverageDeadlineFrom] = useState<string | null>(
    null,
  );
  const [coverageRow, setCoverageRow] =
    useState<PartnerOrganizationCoverageRow | null>(null);
  const [opportunityCounts, setOpportunityCounts] = useState<
    Record<string, number>
  >({});
  const [activityByOrgId, setActivityByOrgId] = useState<
    Record<string, PartnerOrganizationActivity>
  >({});
  const opportunityCountsRequestId = useRef(0);
  const workDrawerOrgRequestId = useRef(0);
  const [displayType, setDisplayType] = useSessionStorage<
    "table" | "map" | "registrations" | "coverage" | "leads"
  >(PARTNER_ORGANIZATIONS_DISPLAY_TYPE_KEY, "coverage");

  const hyperFiltersStorageKey = useMemo(() => {
    return `hyper_table_${repo(PartnerOrganizationEntity).metadata.key}_partner-organizations-table_filters`;
  }, []);
  const [storedHyperFilters] = useSessionStorage<any[]>(
    hyperFiltersStorageKey,
    [],
  );
  const [liveHyperFilters, setLiveHyperFilters] = useState<
    ActiveFilter<PartnerOrganization>[]
  >(storedHyperFilters as ActiveFilter<PartnerOrganization>[]);

  const handleFiltersChange = useCallback(
    (filters: ActiveFilter<PartnerOrganization>[]) => {
      setLiveHyperFilters((prev) =>
        filtersFingerprint(prev) === filtersFingerprint(filters)
          ? prev
          : filters,
      );
    },
    [],
  );

  const filterWhere = useMemo((): EntityFilter<PartnerOrganization> => {
    const values = (liveHyperFilters ?? [])
      .map((f: { value?: unknown }) => f?.value)
      .filter(
        (v): v is Record<string, unknown> =>
          v != null &&
          typeof v === "object" &&
          Object.keys(v as object).length > 0,
      );
    return values.length > 0
      ? ({ $and: values } as EntityFilter<PartnerOrganization>)
      : {};
  }, [liveHyperFilters]);

  const hasPartnerOrgFilter = Object.keys(filterWhere).length > 0;

  const {
    data: partnerOrgTracking = { ids: [] as string[], truncated: false },
    isLoading: isPartnerOrgIdsLoading,
  } = useQuery({
    queryKey: [
      "partner-organizations-filtered-ids",
      JSON.stringify(filterWhere),
    ],
    queryFn: async () => {
      const orgs = await partnerOrganizationRepo.find({
        where: filterWhere,
        orderBy: { id: "asc" },
        limit: PARTNER_ORGANIZATIONS_TRACKING_LIMIT + 1,
      });
      return {
        ids: orgs
          .slice(0, PARTNER_ORGANIZATIONS_TRACKING_LIMIT)
          .map((o) => o.id),
        truncated: orgs.length > PARTNER_ORGANIZATIONS_TRACKING_LIMIT,
      };
    },
    enabled:
      hasPartnerOrgFilter &&
      (displayType === "registrations" || displayType === "coverage"),
    staleTime: 1000 * 30,
  });
  const partnerOrgIds = hasPartnerOrgFilter ? partnerOrgTracking.ids : undefined;
  const partnerOrgIdsTruncated = hasPartnerOrgFilter
    ? partnerOrgTracking.truncated
    : false;

  const loadWorkDrawerOrganization = useCallback(
    async (partnerOrganizationId: string) => {
      const org = await partnerOrganizationRepo.findId(partnerOrganizationId, {
        include: PARTNER_ORG_WORK_DRAWER_INCLUDE,
      });
      return (
        org ?? (await loadPartnerOrganizationForDetails(partnerOrganizationId))
      );
    },
    [],
  );

  const openOpportunities = useCallback(
    (organization: PartnerOrganization) => {
      const requestId = ++workDrawerOrgRequestId.current;
      setCoverageRow(null);
      setCoverageDeadlineFrom(null);
      setOpportunitiesOrganization(organization);
      void loadWorkDrawerOrganization(organization.id).then((loaded) => {
        if (requestId !== workDrawerOrgRequestId.current || !loaded) return;
        setOpportunitiesOrganization(loaded);
      });
    },
    [loadWorkDrawerOrganization],
  );

  const openCoverageOrganization = useCallback(
    async (
      partnerOrganizationId: string,
      row?: PartnerOrganizationCoverageRow,
      registrationDeadlineFrom?: string,
    ) => {
      const requestId = ++workDrawerOrgRequestId.current;
      setCoverageRow(row ?? null);
      setCoverageDeadlineFrom(registrationDeadlineFrom ?? null);
      if (row) {
        setOpportunitiesOrganization({
          id: row.partnerOrganizationId,
          name: row.name,
          logoImageId: row.logoImageId,
          comments: row.comments ?? "",
          cooperationLevel: row.cooperationLevel,
          handledBy: row.handledBy,
          nextFollowUpAt: row.nextFollowUpAt
            ? new Date(row.nextFollowUpAt)
            : null,
        } as PartnerOrganization);
      }
      const loaded = await loadWorkDrawerOrganization(partnerOrganizationId);
      if (requestId !== workDrawerOrgRequestId.current) return;
      if (!loaded) {
        setOpportunitiesOrganization(undefined);
        setCoverageRow(null);
        setCoverageDeadlineFrom(null);
        setAlertText("לא נמצא ארגון שותף");
        setSeverity("error");
        setTime(3000);
        return;
      }
      setOpportunitiesOrganization(loaded);
    },
    [loadWorkDrawerOrganization, setAlertText, setSeverity, setTime],
  );

  const closeOpportunities = useCallback(() => {
    setOpportunitiesOrganization(undefined);
    setCoverageRow(null);
    setCoverageDeadlineFrom(null);
  }, []);

  const handleDisplayedDataChange = useCallback(
    async (data: PartnerOrganization[]) => {
      const ids = data.map((org) => org.id).filter(Boolean);
      const requestId = ++opportunityCountsRequestId.current;
      if (ids.length === 0) {
        setOpportunityCounts({});
        setActivityByOrgId({});
        return;
      }
      try {
        const { activity, openLeavesCount } =
          await fetchPartnerOrganizationCatalogStats(ids);
        if (requestId !== opportunityCountsRequestId.current) return;
        setOpportunityCounts(openLeavesCount);
        setActivityByOrgId(activity);
      } catch (error) {
        console.error("Failed to load partner organization catalog stats", error);
        if (requestId !== opportunityCountsRequestId.current) return;
        setOpportunityCounts({});
        setActivityByOrgId({});
      }
    },
    [],
  );

  const handleCommunicationSaved = useCallback(
    (organizationId: string, records?: CommunicationRecord[]) => {
      const latest = getLatestCommunicationRecord(records);
      setActivityByOrgId((prev) => ({
        ...prev,
        [organizationId]: {
          lastLoginAt: prev[organizationId]?.lastLoginAt ?? null,
          lastNeedAt: prev[organizationId]?.lastNeedAt ?? null,
          lastCommunicationAt: latest?.createdAt
            ? new Date(latest.createdAt)
            : null,
          lastCommunications: latest ? [latest] : [],
        },
      }));
      void queryClient.invalidateQueries({
        queryKey: ["partner-organization-coverage"],
      });
    },
    [queryClient],
  );

  const closeDialog = useCallback(() => {
    setSelectedOrganization(undefined);
    setIsAddOrganization(false);
    setCreateParent(null);
  }, []);

  const openAdd = useCallback(() => {
    setCreateParent(null);
    setSelectedOrganization(undefined);
    setIsAddOrganization(true);
  }, []);

  const openAddChild = useCallback((parent: PartnerOrganization) => {
    setCreateParent({ id: parent.id, name: parent.name });
    setSelectedOrganization(undefined);
    setIsAddOrganization(true);
  }, []);

  const openEdit = useCallback((organization: PartnerOrganization) => {
    setCreateParent(null);
    setSelectedOrganization(organization);
    setIsAddOrganization(false);
  }, []);

  const deletePartnerOrganizations = useCallback(
    async (
      where: EntityFilter<PartnerOrganization>,
      successMessage = "הארגונים השותפים נמחקו בהצלחה",
    ) => {
      try {
        const toDelete = await partnerOrganizationRepo.find({ where });
        const deleteIds = toDelete.map((organization) => organization.id);
        const outsideChildCount =
          deleteIds.length === 0
            ? 0
            : await partnerOrganizationRepo.count({
                parentId: { $in: deleteIds },
                id: { $nin: deleteIds },
              });
        if (outsideChildCount > 0) {
          setAlertText("לא ניתן למחוק ארגון כל עוד יש לו תתי ארגונים");
          setSeverity("error");
          setTime(3000);
          return false;
        }
        const waves = partnerOrganizationDeleteWaves(
          toDelete.map((organization) => ({
            id: organization.id,
            parentId: organization.parentId ?? null,
          })),
        );
        await unlinkManagersForOrgs(deleteIds);
        for (const wave of waves) {
          await partnerOrganizationRepo.deleteMany({
            where: { id: { $in: wave } },
          });
        }
        setAlertText(successMessage);
        setSeverity("success");
        setTime(2000);
        return true;
      } catch {
        setAlertText("מחיקה נכשלה");
        setSeverity("error");
        setTime(3000);
        return false;
      }
    },
    [setAlertText, setSeverity, setTime],
  );

  const getMenuActions = useCallback<SingleRowActionGetter<PartnerOrganization>>(
    () => {
      const actions: SingleRowAction<PartnerOrganization>[] = [
        {
          text: "משימות",
          icon: <AssignmentIcon />,
          onClick: (_: unknown, row: { original: PartnerOrganization }) => {
            openOpportunities(row.original);
            return { refetch: false };
          },
        },
      ];
      if (canManage) {
        actions.push(
          {
            text: "עריכה",
            icon: <EditIcon />,
            onClick: (_: unknown, row: { original: PartnerOrganization }) => {
              openEdit(row.original);
              return { refetch: false };
            },
          },
          {
            text: "מחיקה",
            icon: <DeleteOutlineIcon />,
            onClick: async (_: unknown, row: { original: PartnerOrganization }) => {
              const yes = await question(
                `האם למחוק את הארגון השותף "${row.original.name}"?`,
              );
              if (!yes) return { refetch: false };
              const deleted = await deletePartnerOrganizations(
                { id: row.original.id },
                "הארגון השותף נמחק בהצלחה",
              );
              return { refetch: deleted };
            },
          },
        );
      }
      return actions;
    },
    [canManage, deletePartnerOrganizations, openEdit, openOpportunities, question],
  );

  const columns = useMemo(
    () =>
      buildPartnerOrganizationColumns(
        openOpportunities,
        opportunityCounts,
        activityByOrgId,
        handleCommunicationSaved,
      ),
    [openOpportunities, opportunityCounts, activityByOrgId, handleCommunicationSaved],
  );

  const buttons = useMemo<ActionButton<PartnerOrganization>[]>(
    () => [
      {
        icon: <DeleteOutlineIcon />,
        title: "מחיקה",
        onClick: async ({ whereToUpdate }) => {
          const yes = await question("האם למחוק את הארגונים השותפים שנבחרו?");
          if (!yes) return { refetch: false };
          const deleted = await deletePartnerOrganizations(whereToUpdate);
          return { refetch: deleted, resetSelection: deleted };
        },
      },
    ],
    [deletePartnerOrganizations, question],
  );

  const dialogOpen = isAddOrganization || !!selectedOrganization;
  const isRegistrations = displayType === "registrations";
  const isCoverage = displayType === "coverage";
  const isMap = displayType === "map";
  const isLeads = displayType === "leads";
  const hideTableBody = isRegistrations || isMap || isCoverage || isLeads;

  return (
    <Box
      sx={{
        display: "flex",
        flex: "1 1 0",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "stretch",
        py: 1.5,
        px: 1.5,
        minWidth: 0,
        width: "100%",
        height: "100%",
        overflowY: "auto",
        borderRadius: "15px",
        bgcolor: "background.paper",
        boxSizing: "border-box",
      }}
    >
      <HyperTable
        tableId="partner-organizations-table"
        onFiltersChange={handleFiltersChange}
        components={{
          ...(canManage && !hideTableBody
            ? {
                addRowButton: {
                  onClick: openAdd,
                  content: "ארגון שותף חדש",
                },
              }
            : {}),
          loading: hideTableBody ? null : undefined,
          paginate: hideTableBody ? null : undefined,
          scrollToTop: hideTableBody ? () => null : undefined,
          noResults: hideTableBody ? null : undefined,
        }}
        findConfig={{
          initialOrderBy: { createdAt: "desc" },
          enabled: !hideTableBody,
          include: {
            parent: true,
            handlingManager: true,
            addresses: true,
          },
        }}
        onDisplayedDataChange={
          hideTableBody ? undefined : handleDisplayedDataChange
        }
        onRowClick={
          hideTableBody
            ? undefined
            : (_, row) => openOpportunities(row.original)
        }
        title={<TableHeaderTitles titles={[{ content: "ארגונים שותפים" }]} />}
      >
        <HyperTable.Toolbar disableViewOptions={isLeads}>
          {!isLeads &&
            PARTNER_ORGANIZATION_TABLE_FILTERS.map((filter) =>
              isEntityFilter(filter) ? (
                <HyperTable.EntityFilter key={filter.id} {...(filter as any)} />
              ) : (
                <HyperTable.CustomFilter key={filter.id} {...filter} />
              ),
            )}
          <TableToolbarTabs
            value={displayType}
            onChange={(value) => setDisplayType(value)}
            options={[
              { content: "מענה חסר", value: "coverage" },
              { content: "כל הארגונים", value: "table" },
              { content: "מפה", value: "map" },
              { content: "הרשמות", value: "registrations" },
              { content: "לידים", value: "leads" },
            ]}
          />
        </HyperTable.Toolbar>
        {isLeads ? (
          <PartnerOrganizationLeadsTable
            onOrganizationCreated={async () => {
              await HyperTable.context.current?.refetch("invalidate");
              await queryClient.invalidateQueries({
                queryKey: ["partner-organization-coverage"],
              });
              await queryClient.invalidateQueries({
                queryKey: ["partner-org-activity"],
              });
            }}
          />
        ) : isRegistrations ? (
          isPartnerOrgIdsLoading ? (
            <Stack
              width="100%"
              height="100%"
              paddingTop="10vh"
              alignItems="center"
            >
              <CircularProgress />
            </Stack>
          ) : partnerOrgIds?.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>
              אין ארגונים שותפים להצגת מעקב הרשמות
            </Alert>
          ) : (
            <Stack flex={1} minHeight={0} overflow="hidden">
              {partnerOrgIdsTruncated && (
                <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
                  מוצגים עד {PARTNER_ORGANIZATIONS_TRACKING_LIMIT} ארגונים לפי
                  הסינון הנוכחי. צמצמו את הסינון כדי לכלול את כולם.
                </Alert>
              )}
              <OpportunitiesRegistrationTracking
                hyperFilters={{
                  categoryIds: [],
                  subCategoryId: null,
                  teamIds: [],
                  populationIds: [],
                  partnerOrganizationIds: partnerOrgIds,
                }}
              />
            </Stack>
          )
        ) : isCoverage ? (
          isPartnerOrgIdsLoading ? (
            <Stack
              width="100%"
              height="100%"
              paddingTop="10vh"
              alignItems="center"
            >
              <CircularProgress />
            </Stack>
          ) : partnerOrgIds?.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>
              אין ארגונים שותפים להצגת מענה חסר
            </Alert>
          ) : (
            <Stack flex={1} minHeight={0} overflow="hidden">
              {partnerOrgIdsTruncated && (
                <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
                  מוצגים עד {PARTNER_ORGANIZATIONS_TRACKING_LIMIT} ארגונים לפי
                  הסינון הנוכחי. צמצמו את הסינון כדי לכלול את כולם.
                </Alert>
              )}
              <PartnerOrganizationsCoverage
                partnerOrganizationIds={partnerOrgIds}
                onOpenOrganization={openCoverageOrganization}
                onGoToCatalog={() => setDisplayType("table")}
              />
            </Stack>
          )
        ) : isMap ? (
          <PartnerOrganizationsMap
            filterWhere={filterWhere}
            canEdit={canManage}
            onEdit={openEdit}
            onOpenOrganization={openOpportunities}
          />
        ) : (
          <>
            {canManage && <HyperTable.SelectColumn />}
            <HyperTable.MenuColumn actions={getMenuActions} />
            {columns.map((column) => (
              <HyperTable.Column key={column.field} {...column} />
            ))}
            {canManage && <HyperTable.ActionsBar buttons={buttons} />}
          </>
        )}
      </HyperTable>

      <PartnerOrganizationWorkDrawer
        open={!!opportunitiesOrganization}
        organization={opportunitiesOrganization ?? null}
        coverageRow={coverageRow}
        registrationDeadlineFrom={coverageDeadlineFrom}
        onClose={closeOpportunities}
        onEdit={canManage ? openEdit : undefined}
        onOpenOrganization={openOpportunities}
        onAddChild={canManage ? openAddChild : undefined}
      />

      {canManage && dialogOpen && (
        <PartnerOrganizationDialog
          open={dialogOpen}
          organization={isAddOrganization ? null : selectedOrganization ?? null}
          createDefaults={
            isAddOrganization && createParent
              ? { parentId: createParent.id, parentName: createParent.name }
              : undefined
          }
          onClose={closeDialog}
          onSaved={async (result: PartnerOrganizationSavedResult) => {
            await HyperTable.context.current?.refetch("invalidate");
            await queryClient.invalidateQueries({
              queryKey: ["partner-organization-coverage"],
            });
            await queryClient.invalidateQueries({
              queryKey: ["partner-org-activity"],
            });
            await queryClient.invalidateQueries({
              queryKey: ["partner-org-children"],
            });
            await queryClient.invalidateQueries({
              queryKey: ["partner-organization-parent-options"],
            });
            if (opportunitiesOrganization?.id) {
              const loaded = await loadWorkDrawerOrganization(
                opportunitiesOrganization.id,
              );
              if (loaded) setOpportunitiesOrganization(loaded);
            }
            setAlertText(
              result.created ? "הארגון נוצר בהצלחה" : "הארגון עודכן בהצלחה",
            );
            setSeverity("success");
            setTime(2000);
            setSelectedOrganization(result.organization);
            setIsAddOrganization(false);
            if (result.close) {
              closeDialog();
            }
          }}
        />
      )}
    </Box>
  );
};

export default PartnerOrganizationsTable;
